import { showConfirmation } from '../lib/confirmation';
import { formatDate as format, formatName, formatTime, formatPhone } from '../lib/format';
import PageContent from '../components/ui/PageContent';
import PageHeader from '../components/ui/PageHeader';
import { TableEmpty, Table, TableHead, TableRow, TableHeaderCell, TableCell } from '../components/ui/Table';
import UiCheckbox from '../components/ui/Checkbox';
import Button from '../components/ui/Button';
import { getAllocationDisplayAddress } from '../utils/workerAllocationAddress';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Section from '../components/Section';
import LoadingPopup from '../components/LoadingPopup';
import ModalComponent from '../components/Modal';
import TimesheetPdfGenerator from '../components/TimesheetPdfGenerator';
import MyCalendar from '../components/Calendar';
import Input from '../components/Input';
import { DownloadIcon } from '../components/NavIcons';
import { requestTimesheetLocation } from '../utils/timesheetLocation';
import { getQuarterHoursFromTimes, postWithAuth } from '../common';
import { businessWallDateToIso, toBusinessWallDate } from '../utils/businessTime';
const getNormalizedStatus = row => String(row?.status || '').toLowerCase()

const isHistoricalIncompleteTimesheet = (row, todayStart) => {
    const status = getNormalizedStatus(row)
    if (row?.clockIn && row?.clockOut) return false
    if (row?.clockIn && !row?.clockOut) return true
    if (status === 'active') return true
    if (status === 'completed') return false
    if (status === 'accepted') return (toBusinessWallDate(row?.date) || new Date(0)) < todayStart
    return false
}


const WorkerTimesheets = () => {
    const activeOrg = useSelector(state => state.user.activeOrg)
    const activeOrgInfo = useSelector(state => state.user.activeOrgInfo)
    const userInfo = useSelector(state => state.user.user)
    const [timeSheets, setTimeSheets] = useState([])
    const [loading, setLoading] = useState(false)
    const [viewTimeSheet, setViewTimeSheet] = useState(null)
    const [downloadTimeSheet, setDownloadTimeSheet] = useState(null)
    const [downloadQueue, setDownloadQueue] = useState([])
    const [selectedTimeSheetIds, setSelectedTimeSheetIds] = useState([])
    const [clockIn, setClockIn] = useState('')
    const [clockOut, setClockOut] = useState('')
    const [breakMinutes, setBreakMinutes] = useState('0')
    const [timesheetFilterDate, setTimesheetFilterDate] = useState('')

    useEffect(() => {
        if (!activeOrg?.id) return
        const loadTimeSheets = async () => {
            try {
                setLoading(true)
                const response = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id })
                if (response?.err) throw new Error(response.err)
                setTimeSheets(response?.data || [])
            } catch (error) {
                toast.error(error.message)
            } finally {
                setLoading(false)
            }
        }
        loadTimeSheets()
    }, [activeOrg?.id])

    const pastAllocations = useMemo(() => {
        const today = toBusinessWallDate(new Date())
        today.setHours(0, 0, 0, 0)
        return timeSheets
            .filter(timeSheet => (toBusinessWallDate(timeSheet.date) || new Date(0)) < today)
            .sort((first, second) => new Date(second.date) - new Date(first.date))
    }, [timeSheets])
    const filteredPastAllocations = useMemo(() => {
        if (!timesheetFilterDate) return pastAllocations
        const selectedDate = new Date(timesheetFilterDate)
        return pastAllocations.filter(timeSheet => {
            const allocationDate = new Date(timeSheet.date)
            return allocationDate.getFullYear() === selectedDate.getFullYear()
                && allocationDate.getMonth() === selectedDate.getMonth()
                && allocationDate.getDate() === selectedDate.getDate()
        })
    }, [pastAllocations, timesheetFilterDate])

    const renderDateTime = value => {
        if (!value) return '-'
        const date = new Date(value)
        if (Number.isNaN(date.getTime())) return '-'
        return <><span>{format(date, 'EEE, d MMM yyyy')}</span><br /><span>{formatTime(date)}</span></>
    }
    const formatPopupDate = (value, includeTime = false) => {
        if (!value) return '-'
        const date = new Date(value)
        const weekday = format(date, 'EEE').replace(/^Tue$/, 'Tues').replace(/^Thu$/, 'Thur')
        const dateText = `${weekday}, ${format(date, 'd MMM yyyy')}`
        return includeTime ? `${dateText} - ${formatTime(date)}` : dateText
    }
    const getSiteManagerName = timeSheet => {
        if (timeSheet?.supervisorName) return timeSheet.supervisorName
        const details = String(timeSheet?.siteManagerDetails || '')
        return details
            .replace(/\+?\d[\d\s()-]{7,}\d/g, '')
            .replace(/[|,·-]+\s*$/, '')
            .trim() || '-'
    }
    const isFutureCalendarDate = (value) => {
        if (!value) return false
        const selectedDate = new Date(value)
        const today = toBusinessWallDate(new Date())
        return selectedDate.getFullYear() > today.getFullYear()
            || (selectedDate.getFullYear() === today.getFullYear() && selectedDate.getMonth() > today.getMonth())
            || (selectedDate.getFullYear() === today.getFullYear() && selectedDate.getMonth() === today.getMonth() && selectedDate.getDate() > today.getDate())
    }
    const setShiftDateWithWarning = (value, setter) => {
        if (value && viewTimeSheet?.date) {
            const selectedDate = new Date(value)
            const timesheetDate = new Date(viewTimeSheet.date)
            const isDifferentDate = selectedDate.getFullYear() !== timesheetDate.getFullYear()
                || selectedDate.getMonth() !== timesheetDate.getMonth()
                || selectedDate.getDate() !== timesheetDate.getDate()
            if (isDifferentDate) {
                showConfirmation('The selected shift date is different from the timesheet date. You can continue if this is correct.', { tone: 'warning' })
            }
        }
        setter(value)
    }
    const isLocked = getNormalizedStatus(viewTimeSheet) === 'completed' || !!viewTimeSheet?.clockOut
    const [fetchingDownload, setFetchingDownload] = useState(false)
    const loadTimeSheetDetails = async (timeSheet, action, { quiet = false } = {}) => {
        setFetchingDownload(action === setDownloadTimeSheet)
        try {
            setLoading(true)
            const response = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id, timeSheetId: timeSheet.id })
            if (response?.err) throw new Error(response.err)
            const detailedTimeSheet = response?.data?.[0]
            if (!detailedTimeSheet) throw new Error('Timesheet not found.')
            action(detailedTimeSheet)
            return true
        } catch (error) {
            if (!quiet) toast.error(error.message)
            return false
        } finally {
            setFetchingDownload(false)
            setLoading(false)
        }
    }

    useEffect(() => {
        if (!viewTimeSheet) {
            setClockIn('')
            setClockOut('')
            setBreakMinutes('0')
            return
        }
        const isUnstarted = !viewTimeSheet.clockIn && !viewTimeSheet.clockOut
            setClockIn(!isUnstarted && viewTimeSheet.clockIn ? toBusinessWallDate(viewTimeSheet.clockIn) : '')
            setClockOut(!isUnstarted && viewTimeSheet.clockOut ? toBusinessWallDate(viewTimeSheet.clockOut) : '')
        setBreakMinutes(viewTimeSheet.breakMinutes !== undefined ? String(viewTimeSheet.breakMinutes) : '0')
    }, [viewTimeSheet])

    const handleCloseViewTimeSheet = () => {
        setViewTimeSheet(null)
        setClockIn('')
        setClockOut('')
        setBreakMinutes('0')
    }

    const refreshTimeSheets = async () => {
        const response = await postWithAuth('fetchTimeSheet', { orgId: activeOrg.id })
        if (response?.err) throw new Error(response.err)
        setTimeSheets(response?.data || [])
    }

    const handleSaveTimeSheet = async () => {
        if (!viewTimeSheet?.id || isLocked) return
        try {
            setLoading(true)
            const breakValue = Number(breakMinutes) || 0
            if (breakValue < 0 || breakValue % 15 !== 0) {
                throw new Error('Break duration must be a non-negative multiple of 15 minutes.')
            }
            if (!clockIn || !clockOut) {
                throw new Error('Enter both shift start and shift end.')
            }
            if (clockOut <= clockIn) {
                throw new Error('Clock out time must be after clock in time.')
            }
            if ((new Date(clockOut).getTime() - new Date(clockIn).getTime()) < 4 * 60 * 60 * 1000) {
                throw new Error('Change finish time. Minimum 4 hour shift required.')
            }
            if (isFutureCalendarDate(clockIn) || isFutureCalendarDate(clockOut)) {
                throw new Error('Change shift start/end date. Cannot be a future date.')
            }

            const locationCoordinates = await requestTimesheetLocation()

            const saveResponse = await postWithAuth('saveTimeSheet', {
                id: viewTimeSheet.id,
                clockIn: businessWallDateToIso(clockIn),
                clockOut: businessWallDateToIso(clockOut),
                orgId: activeOrg.id,
                breakMinutes: breakValue,
                breakIncludedInPay: !!viewTimeSheet.breakIncludedInPay,
                travelAllowanceApplied: !!viewTimeSheet.travelAllowanceApplied,
                supervisorName: viewTimeSheet.supervisorName || '',
                supervisorNotAvailable: !!viewTimeSheet.supervisorNotAvailable,
                supervisorSign: viewTimeSheet.supervisorSign || '',
                locationCoordinates,
            })
            if (saveResponse?.err) throw new Error(saveResponse.err)

            await refreshTimeSheets()
            showConfirmation('Timesheet information saved')
            handleCloseViewTimeSheet()
        } catch (error) {
            toast.error(error.message)
        } finally {
            setLoading(false)
        }
    }

    const completedRows = filteredPastAllocations.filter(row => row.clockIn && row.clockOut)
    const toggleSelectedTimesheet = id => setSelectedTimeSheetIds(current => current.includes(id) ? current.filter(value => value !== id) : [...current, id])
    // Selected ids are looked up in every past timesheet, not just the rows the
    // date filter shows, and one that cannot be loaded is skipped rather than
    // ending the whole run. The failures are reported once at the end.
    const downloadFailuresRef = useRef(0)
    const finishSelectedDownload = () => {
        const failed = downloadFailuresRef.current
        downloadFailuresRef.current = 0
        if (failed) toast.error(`${failed} selected timesheet${failed === 1 ? '' : 's'} could not be downloaded.`)
    }
    const downloadNextSelected = async queue => {
        let pending = queue
        while (pending.length) {
            const [nextId, ...remaining] = pending
            setDownloadQueue(remaining)
            const row = pastAllocations.find(item => item.id === nextId && item.clockIn && item.clockOut)
            // On success the PDF generator's onComplete carries on with the queue.
            if (row && await loadTimeSheetDetails(row, setDownloadTimeSheet, { quiet: true })) return
            downloadFailuresRef.current += 1
            pending = remaining
        }
        finishSelectedDownload()
    }
    const startSelectedDownload = () => {
        downloadFailuresRef.current = 0
        downloadNextSelected(selectedTimeSheetIds)
    }
    return (
        <div>
            <Header />
            <LoadingPopup visible={loading} message={fetchingDownload ? 'Downloading timesheet...' : 'Loading...'} />
            {downloadTimeSheet && <TimesheetPdfGenerator
                timesheet={downloadTimeSheet}
                workerName={downloadTimeSheet.workerName || downloadTimeSheet.worker || ''}
                logo={activeOrgInfo?.logo?.url}
                onComplete={() => {
                    setDownloadTimeSheet(null)
                    if (downloadQueue.length) downloadNextSelected(downloadQueue)
                    else finishSelectedDownload()
                }}
            />}
            <div className='pageShell'>
                <Sidebar />
                <PageContent as="div" className='pageContent pageContentPadded workerPageContent workerPortalPage'>
                    <PageHeader title={<>Timesheets</>} subtitle={<>Past allocations and recorded shift times</>} />
                    <Section title='Timesheets' className='width-100' variant='card'>
                        <div className='workerTimesheetDateFilter'>
                            <MyCalendar
                                showDateOnly
                                noMaxDate
                                showMonthDropdown
                                showYearDropdown
                                dropdownMode='select'
                                todayButton='Show today'
                                calendarStartDay={1}
                                calendarClassName='workerTimesheetReferencePicker'
                                useAppCalendarPicker
                                startDate={timesheetFilterDate}
                                setStartDate={setTimesheetFilterDate}
                                label='Filter by date'
                            />
                            {timesheetFilterDate && <Button variant="secondary" size="md" type='button' className='textButton' onClick={() => setTimesheetFilterDate('')}>Clear</Button>}
                        </div>
                        <div className='workerTimesheetDateFilter'>
                            <Button variant="secondary" size="md" type='button' className='secondaryButton' disabled={!selectedTimeSheetIds.length} onClick={startSelectedDownload}>Download Selected</Button>
                            <span>{selectedTimeSheetIds.length} selected</span>
                        </div>
                        <div className='workerAllocationTableWrap workerTimesheetsTableWrap'>
                            <Table className='workerAllocationTable'>
                                <TableHead><TableRow><TableHeaderCell><UiCheckbox type='checkbox' aria-label='Select all completed timesheets' checked={completedRows.length > 0 && completedRows.every(row => selectedTimeSheetIds.includes(row.id))} onChange={event => setSelectedTimeSheetIds(event.target.checked ? completedRows.map(row => row.id) : [])} /></TableHeaderCell><TableHeaderCell>Allocation Date</TableHeaderCell><TableHeaderCell>Client</TableHeaderCell><TableHeaderCell>Site</TableHeaderCell><TableHeaderCell>Shift Start</TableHeaderCell><TableHeaderCell>Shift End</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHead>
                                <tbody>
                                    {filteredPastAllocations.map(timeSheet => (
                                        <TableRow
                                            key={timeSheet.id}
                                            className={`${isHistoricalIncompleteTimesheet(timeSheet, new Date(new Date().setHours(0,0,0,0)))
                                                ? 'workerTimesheetRowIncomplete'
                                                : getNormalizedStatus(timeSheet) === 'accepted'
                                                    ? 'workerTimesheetRowAccepted'
                                                    : ''}`.trim()}
                                        >
                                            <TableCell data-label='Select'><UiCheckbox type='checkbox' aria-label='Select timesheet' disabled={!timeSheet.clockIn || !timeSheet.clockOut} checked={selectedTimeSheetIds.includes(timeSheet.id)} onChange={() => toggleSelectedTimesheet(timeSheet.id)} /></TableCell>
                                            <TableCell data-label='Allocation Date' className='workerTimesheetDateTimeLink' role='button' tabIndex={0} onClick={() => loadTimeSheetDetails(timeSheet, setViewTimeSheet)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') loadTimeSheetDetails(timeSheet, setViewTimeSheet) }}>{renderDateTime(timeSheet.date)}</TableCell>
                                            <TableCell data-label='Client'>{timeSheet.clientName || timeSheet.client || '-'}</TableCell>
                                            <TableCell data-label='Site'>{timeSheet.siteName || timeSheet.site || '-'}</TableCell>
                                            <TableCell data-label='Shift Start'>{renderDateTime(timeSheet.clockIn)}</TableCell>
                                            <TableCell data-label='Shift End'>{renderDateTime(timeSheet.clockOut)}</TableCell>
                                            <TableCell data-label='Status'>{isHistoricalIncompleteTimesheet(timeSheet, new Date(new Date().setHours(0,0,0,0))) ? 'Incomplete' : (timeSheet.status || 'Pending')}</TableCell>
                                            <TableCell data-label='Action' className='workerTimesheetActionColumn'>
                                                {!timeSheet.clockOut && getNormalizedStatus(timeSheet) !== 'completed' && <Button variant="secondary" size="sm" type='button' className='secondaryButton' onClick={() => loadTimeSheetDetails(timeSheet, setViewTimeSheet)}>Complete timesheet</Button>}
                                                {timeSheet.status === 'Completed' && <Button variant="secondary" size="icon" type='button' title='Download timesheet' aria-label='Download timesheet' className='workerTimesheetAction workerTimesheetActionDownload' onClick={event => { event.stopPropagation(); loadTimeSheetDetails(timeSheet, setDownloadTimeSheet) }}><DownloadIcon style={{ width: 21, height: 21 }} /></Button>}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!loading && filteredPastAllocations.length === 0 && <TableEmpty  colSpan={8}>No timesheets yet.</TableEmpty>}
                                </tbody>
                            </Table>
                        </div>
                    </Section>
                    <ModalComponent showModal={!!viewTimeSheet} setShowModal={setViewTimeSheet} customMaxWidth='920px' customContentStyle={{ boxSizing: 'border-box', minWidth: 0, width: 'calc(100% - 24px)' }}>
                        <h2 className='modalTitle'>Timesheet Details</h2>
                        <div className='workerTimesheetDetailSections'>
                            <Section title='Site' className='width-100' variant='card'>
                                <div className='detailGrid workerTimesheetSiteGrid'>
                                    <div className='detailItem'><span className='detailLabel'>Allocation ID</span><span className='detailValue'>{viewTimeSheet?.allocationId || viewTimeSheet?.allocationNumber || '-'}</span></div>
                                    <div className='detailItem'><span className='detailLabel'>Date/Time</span><span className='detailValue'>{formatPopupDate(viewTimeSheet?.date, true)}</span></div>
                                    <div className='detailItem'><span className='detailLabel'>Client Name</span><span className='detailValue'>{viewTimeSheet?.clientName || viewTimeSheet?.client || '-'}</span></div>
                                    <div className='detailItem'><span className='detailLabel'>Site Name</span><span className='detailValue'>{viewTimeSheet?.siteName || viewTimeSheet?.site || '-'}</span></div>
                                    <div className='detailItem'><span className='detailLabel'>Address</span><span className='detailValue'>{getAllocationDisplayAddress(viewTimeSheet)}</span></div>
                                    <div className='detailItem'><span className='detailLabel'>Scope of Works</span><span className='detailValue'>{viewTimeSheet?.scopeOfWorks || '-'}</span></div>
                                </div>
                            </Section>

                            <div className='workerTimesheetPeopleGrid'>
                            <Section title='Workers' className='width-100' variant='card'>
                                <div className='modalList'>
                                    {(Array.isArray(viewTimeSheet?.chosenWorkerNames) ? viewTimeSheet.chosenWorkerNames : []).map((worker, index) => (
                                        <div key={`worker-timesheet-worker-${worker.id || index}`} className='modalListRow'>
                                            <div className='modalListName'>
                                                {worker.name || `Worker ${index + 1}`}
                                                {worker.role ? ` (${worker.role})` : ''}
                                                {worker.assetName && <span className='detailValue' style={{ display: 'block', color: '#64748b', fontWeight: 400 }}>Assets: {worker.assetName}</span>}
                                            </div>
                                            <div>{worker.contactNo ? <a href={`tel:${worker.contactNo}`}>{formatPhone(worker.contactNo)}</a> : <span className='modalEmpty'>No phone number</span>}</div>
                                        </div>
                                    ))}
                                    {(!Array.isArray(viewTimeSheet?.chosenWorkerNames) || viewTimeSheet.chosenWorkerNames.length === 0) && (
                                        <div className='modalListRow'>
                                            <div className='modalListName'>
                                                {viewTimeSheet?.workerName || viewTimeSheet?.worker || formatName([userInfo?.firstName, userInfo?.lastName].filter(Boolean).join(' ')) || userInfo?.name || 'Worker'}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </Section>

                            <Section title='Site Supervisor' className='width-100' variant='card'>
                                <div className='workerTimesheetSupervisorStack'>
                                    <div className='detailItem'><span className='detailLabel'>No one on site to sign off</span><span className='detailValue'>{viewTimeSheet?.supervisorNotAvailable ? 'Yes' : 'No'}</span></div>
                                    {!viewTimeSheet?.supervisorNotAvailable && <>
                                        <div className='detailItem'><span className='detailLabel'>Supervisor Name</span><span className='detailValue'>{formatName(viewTimeSheet?.supervisorName || '-')}</span></div>
                                        {viewTimeSheet?.supervisorSign && <div className='workerTimesheetSignatureWrap'><img src={viewTimeSheet.supervisorSign} alt='Supervisor signature' style={{ width: '100%', maxWidth: '320px', height: '160px', objectFit: 'contain' }} /></div>}
                                    </>}
                                </div>
                            </Section>
                            </div>

                            <Section title='Shift Details' className='width-100' variant='card'>
                                <div className='workerTimesheetEntryStack'>
                                    <div className='workerTimesheetFieldGrid workerTimesheetDetailFieldGrid'>
                                    {isLocked ? (
                                        <>
                                            <MyCalendar nativeOnMobile noMaxDate disabled startDate={clockIn} setStartDate={setClockIn} label='Shift Start' timeIntervals={15} />
                                            <MyCalendar nativeOnMobile noMaxDate disabled startDate={clockOut} setStartDate={setClockOut} label='Shift End' timeIntervals={15} />
                                            <Input disabled type='number' inputMode='numeric' value={breakMinutes} setValue={setBreakMinutes} placeholder='0' label='Break (mins)' />
                                            <Input disabled type='number' label='Total' value={clockIn && clockOut ? getQuarterHoursFromTimes({ clockIn, clockOut, breakMinutes, breakIncludedInPay: !!viewTimeSheet?.breakIncludedInPay, minuteOffset: 1 }) : ''} />
                                        </>
                                    ) : (
                                        <>
                                            <div className='workerTimesheetEditorField'>
                                                <MyCalendar
                                                    nativeOnMobile
                                                    noMaxDate
                                                    disabled={false}
                                                    startDate={clockIn}
                                                    setStartDate={(value) => setShiftDateWithWarning(value, setClockIn)}
                                                    label='Shift Start'
                                                    timeIntervals={15}
                                                    dateFormat='d MMMM yyyy · h:mm a'
                                                    calendarClassName='timesheetCompactDateTimePicker'
                                                />
                                            </div>
                                            <div className='workerTimesheetEditorField'>
                                                <MyCalendar
                                                    nativeOnMobile
                                                    noMaxDate
                                                    disabled={false}
                                                    startDate={clockOut}
                                                    setStartDate={(value) => setShiftDateWithWarning(value, setClockOut)}
                                                    label='Shift End'
                                                    timeIntervals={15}
                                                    dateFormat='d MMMM yyyy · h:mm a'
                                                    calendarClassName='timesheetCompactDateTimePicker'
                                                />
                                            </div>
                                            <div className='workerTimesheetEditorField'>
                                                <Input disabled={false} type='number' inputMode='numeric' value={breakMinutes} setValue={setBreakMinutes} placeholder='0' label='Break (mins)' />
                                            </div>
                                            <div className='workerTimesheetEditorField'>
                                                <Input
                                                    disabled
                                                    type='number'
                                                    label='Total'
                                                    value={clockIn && clockOut ? getQuarterHoursFromTimes({ clockIn, clockOut, breakMinutes, breakIncludedInPay: !!viewTimeSheet?.breakIncludedInPay, minuteOffset: 1 }) : ''}
                                                />
                                            </div>
                                        </>
                                    )}
                                    </div>
                                </div>
                            </Section>

                        </div>
                        <div className='modalFooter'>
                            <Button data-action-tone="cancel" variant="secondary" size="md"  className='secondaryButton' onClick={handleCloseViewTimeSheet}>{isLocked ? 'Cancel' : 'Cancel'}</Button>
                            {!isLocked && <Button data-action-tone="save" variant="soft" size="md"  className='saveWithIcon' disabled={loading || !clockIn || !clockOut} onClick={handleSaveTimeSheet}>Save</Button>}
                        </div>
                    </ModalComponent>
                </PageContent>
            </div>
        </div>
    )
}

export default WorkerTimesheets
